import React, { useMemo } from 'react';
import {
  Typography,
  Card,
  CardMedia,
  Paper,
  Button,
  CardContent,
  Box,
  Tooltip
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Skeleton } from '@mui/material';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import { FormattedMessage } from 'react-intl';
import { Collector, CollectorType } from 'graphql/types';
import { CollectorStatus } from './CollectorStatus';

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.spacing(1),
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(2),
    height: '100%',
    width: theme.spacing(25),
    borderColor: 'rgba(0,0,0,0.12)',
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      boxShadow: '0 0 5px rgba(33,33,33,.3)'
    }
  },
  media: {
    borderRadius: theme.spacing(0),
    height: theme.spacing(15),
    display: 'flex'
  },
  collectorIcon: {
    marginLeft: theme.spacing(7),
    maxHeight: theme.spacing(11),
    maxWidth: theme.spacing(11),
    flexBasis: theme.spacing(11),
    alignSelf: 'center',
    display: 'flex'
  },
  icon: {
    marginLeft: 'auto',
    marginRight: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    height: theme.spacing(3),
    width: theme.spacing(3),
    justifyContent: 'flex-end'
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical'
  },
  text: {
    lineHeight: 1.2,
    width: 'auto',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary
  },
  button: {
    marginTop: 'auto',
    borderRadius: theme.spacing(0),
    padding: theme.spacing(1),
    width: '100%',
    backgroundColor: ({ isInstalled }: { isInstalled: boolean }) =>
      isInstalled ? theme.palette.grey[600] : theme.palette.primary.main
  },
  cardContent: {
    padding: ({ isInstalled }: { isInstalled: boolean }) => {
      return theme.spacing(isInstalled ? 0 : 2, 2);
    },
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(1),
    justifyContent: 'space-between'
  },
  connected: {
    padding: theme.spacing(0, 0.8),
    ...theme.typography.caption,
    color: theme.palette.success.light,
    backgroundColor: theme.palette.success.dark,
    marginLeft: theme.spacing(1.5),
    float: 'left',
    position: 'relative',
    bottom: theme.spacing(1.2),
    top: theme.spacing(-1.2),
    width: 'fit-content',
    borderRadius: theme.spacing(0.5)
  },
  cardAction: {
    marginTop: 'auto',
    height: theme.spacing(5),
    borderRadius: theme.spacing(0),
    backgroundColor: 'rgb(219, 219, 219)',
    padding: theme.spacing(1.25, 2, 0)
  },
  skeletonButton: {
    background: theme.skelton?.color,
    borderRadius: theme.spacing(0.5),
    height: theme.spacing(2.5)
  }
}));

type CollectorsInfoProps = {
  info: Collector;
  syncStatus: string | undefined;
  date: string;
  installId: string | undefined;
  isInstalled: boolean | undefined;
  onConnectClick: (collector: Collector) => void;
  onSettingClick: (collector: Collector) => void;
};

const CollectorsInfo: React.FC<CollectorsInfoProps> = ({
  info,
  date,
  installId,
  syncStatus,
  isInstalled,
  onConnectClick,
  onSettingClick
}: CollectorsInfoProps) => {
  const classes = useStyles({
    isInstalled: !!isInstalled
  });
  const { type } = info;
  const isDesktop = type === CollectorType.Desktop;
  const isPlugin = type === CollectorType.Plugin;
  const collectorIconType = useMemo(() => {
    if (info.type === CollectorType.Cloud)
      return (
        <CloudOutlinedIcon
          data-testid="collector-cloudIcon"
          className={classes.icon}
        />
      );
    else if (info.type === CollectorType.Bot)
      return (
        <ExtensionOutlinedIcon
          data-testid="collector-botIcon"
          className={classes.icon}
        />
      );
    else if (info.type === CollectorType.Desktop)
      return (
        <DesktopWindowsOutlinedIcon
          data-testid="collector-desktopIcon"
          className={classes.icon}
        />
      );
    else if (info.type === CollectorType.Plugin)
      return (
        <DesktopWindowsOutlinedIcon
          data-testid="collector-desktopIcon"
          className={classes.icon}
        />
      );
  }, [classes.icon, info.type]);

  const buttonLoadingSkeleton = useMemo(() => {
    return (
      <Box
        data-testid="collector-button-loading"
        className={classes.cardAction}
      >
        <Skeleton className={classes.skeletonButton} variant="rectangular" />
      </Box>
    );
  }, [classes.skeletonButton, classes.cardAction]);
  return (
    <Card className={classes.card} elevation={0} variant="outlined">
      <CardMedia>
        <Paper
          className={classes.media}
          data-testid="collector-icon"
          elevation={0}
        >
          <img className={classes.collectorIcon} src={info.image.src} />
          {collectorIconType}
        </Paper>
      </CardMedia>
      {isInstalled && (
        <div>
          <span
            className={classes.connected}
            data-testid={'connected-button-' + info.name}
          >
            <FormattedMessage id="collectors.connected" />
          </span>
          {syncStatus && installId && (
            <CollectorStatus
              onClick={() => onSettingClick(info)}
              syncStatus={syncStatus}
              installId={installId}
            />
          )}
        </div>
      )}
      <CardContent className={classes.cardContent}>
        <Tooltip title={info.name} data-testid="collector-title-tooltip">
          <Typography className={classes.title} variant="body1">
            {info.name}
          </Typography>
        </Tooltip>
        <Typography
          className={classes.text}
          variant="caption"
          color="textSecondary"
        >
          <div>
            <FormattedMessage
              id="collectors.publishedBy"
              values={{ by: info.publishedBy }}
            />
          </div>
          <div>
            <FormattedMessage
              id="collectors.categoryText"
              values={{ category: info.category }}
            />
          </div>
          <div>{date}</div>
        </Typography>
      </CardContent>
      {isInstalled || isDesktop || isPlugin ? (
        <Button
          className={classes.button}
          data-testid={'settings-button-' + info.name}
          variant="contained"
          color="primary"
          onClick={() => onSettingClick(info)}
        >
          {isDesktop || isPlugin ? (
            <FormattedMessage id="collectors.details" />
          ) : (
            <FormattedMessage id="collectors.settings" />
          )}
        </Button>
      ) : isInstalled === undefined ? (
        buttonLoadingSkeleton
      ) : (
        <Button
          className={classes.button}
          data-testid={'install-button-' + info.name}
          variant="contained"
          color="primary"
          onClick={() => onConnectClick(info)}
        >
          <FormattedMessage id="collectors.install" />
        </Button>
      )}
    </Card>
  );
};
export default CollectorsInfo;
